/**
 * The console's router: the browser's own history, no library. Each page is a real path (`/support`, `/caps`), so Back
 * and Forward move between pages and every page can be bookmarked or linked. The shapes that existed before the router
 * keep working: `/support?request=<id>` (the desktop app's IT support card) and `/?tab=<id>` (lib/deepLink.ts).
 *
 * gyro-svc answers index.html for every GET outside /v1, /auth, /oauth, /mcp, /embed, /office/ and /healthz
 * (services/gyro-svc/src/server.ts serveAdminSpa), which is what lets a reload land on any of these paths.
 */
import { useSyncExternalStore, type MouseEvent } from 'react';

export interface Place {
  pathname: string;
  search: string;
}

const NAVIGATE = 'gyro-admin:navigate';
const listeners = new Set<() => void>();
let cached: Place | null = null;

function read(): Place {
  const { pathname, search } = window.location;
  if (!cached || cached.pathname !== pathname || cached.search !== search) cached = { pathname, search };
  return cached;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener('popstate', notify);
    window.addEventListener(NAVIGATE, notify);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('popstate', notify);
      window.removeEventListener(NAVIGATE, notify);
    }
  };
}

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** The current path and query; re-renders on navigation, Back and Forward. */
export function usePlace(): Place {
  return useSyncExternalStore(subscribe, read, read);
}

/** Go to `to` (a same-origin path with an optional query). `replace` rewrites the current history entry instead. */
export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  const target = new URL(to, window.location.href);
  if (target.origin !== window.location.origin) return;
  const next = `${target.pathname}${target.search}${target.hash}`;
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== here) {
    if (opts.replace) window.history.replaceState(window.history.state, '', next);
    else window.history.pushState(null, '', next);
  }
  window.dispatchEvent(new Event(NAVIGATE));
}

/**
 * An <a href> click handler that navigates in place: a plain left click with no modifier stays in the console; a
 * middle click or Ctrl/Cmd/Shift click keeps the browser's own behaviour (a new tab or window).
 */
export function linkClick(event: MouseEvent<HTMLAnchorElement>, onNavigate?: () => void): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = event.currentTarget;
  if (anchor.target && anchor.target !== '_self') return;
  event.preventDefault();
  navigate(anchor.getAttribute('href') ?? '/');
  onNavigate?.();
}
